import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useCartStore } from "./store/cartStore";
import { useAuth } from "./AuthContext";
import { useTheme } from "./ThemeContext";
import { paymentService, orderService } from "./services/api";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY || "pk_test_51RXlfBQLSlXJyGAFHNvu7dJgpMeZcF9yLYXk7oWzmGaGAnRfQ6oTUdt6W45AnxCSirgk8jjmBfNYRyWionvexQ2Y00t3FnzRda",
);

function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [tipOption, setTipOption] = useState("none"); // none, 10, 15, 20, custom
  const [customTip, setCustomTip] = useState(0);
  const total = useCartStore((state) => state.total);
  const clearCart = useCartStore((state) => state.clearCart);
  const items = useCartStore((state) => state.items);
  const [formData, setFormData] = useState({
    email: user?.email || "",
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    county: "", // Added county for Dorchester identification
  });
  const [taxDetails, setTaxDetails] = useState(null);

  useEffect(() => {
    // Basic validation before calling backend
    const state = formData.state?.trim() || "SC";
    const zip = formData.zip?.trim() || "29485";
    
    if (total <= 0) {
      setTaxDetails(null);
      return;
    }

    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      
      console.log(`🔍 Requesting tax for ${state}, ${zip}...`);
      
      paymentService.createPaymentIntent({
        amount: Math.round(total * 100),
        items: items,
        shippingAddress: { 
          ...formData, 
          state: state.toUpperCase(),
          zip: zip
        }
      })
        .then((res) => {
          const data = res.data;
          console.log("✅ Tax result received:", data.taxDetails);
          if (data.clientSecret) {
            setClientSecret(data.clientSecret);
            setTaxDetails(data.taxDetails);
          } else if (data.error) {
            setError(data.error);
          }
        })
        .catch((err) => {
          console.error("❌ Tax calculation error:", err);
          setError(`Tax calculation failed: ${err.response?.data?.error || err.message}`);
        })
        .finally(() => setLoading(false));
    }, 800); // 800ms debounce to avoid overwhelming the server

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, formData.state, formData.zip, formData.city, formData.county, items]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setError("Payment system not ready. Please refresh and try again.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: submitError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          address: {
            line1: formData.address,
            city: formData.city,
            state: formData.state,
            postal_code: formData.zip,
          },
        },
      });

      if (submitError) {
        setError(submitError.message);
        setLoading(false);
        return;
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id,
      });

      if (confirmError) {
        setError(confirmError.message);
      } else if (paymentIntent.status === "succeeded") {
        try {
          const orderResponse = await orderService.create({
            userEmail: formData.email,
            total: (total + (taxDetails?.totalTax || 0) / 100),
            subtotal: total,
            taxAmount: (taxDetails?.totalTax || 0) / 100,
            salesTax: (taxDetails?.salesTax || 0) / 100,
            hospitalityTax: (taxDetails?.hospitalityTax || 0) / 100,
            items: items,
            shippingAddress: formData,
            taxBreakdown: taxDetails?.taxBreakdown,
            tipAmount: calculatedTip,
            paymentMethod: "stripe",
            orderType: "online"
          });
          clearCart();
          const orderData = {
            id: orderResponse.data?.id,
            userEmail: formData.email,
            total: (total + (taxDetails?.totalTax || 0) / 100 + calculatedTip),
            subtotal: total,
            taxAmount: (taxDetails?.totalTax || 0) / 100,
            salesTax: (taxDetails?.salesTax || 0) / 100,
            hospitalityTax: (taxDetails?.hospitalityTax || 0) / 100,
            tipAmount: calculatedTip,
            items: items,
            shippingAddress: formData,
            taxBreakdown: taxDetails?.taxBreakdown,
            created_at: new Date().toISOString()
          };
          navigate("/order-confirmation", { state: { order: orderData } });
        } catch (orderError) {
          console.error("Order creation failed:", orderError);
          setError("Payment succeeded but order creation failed. Please contact support.");
        }
      } else {
        setError(`Payment status: ${paymentIntent.status}`);
      }
    } catch (err) {
      setError(err.message || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const calculatedTip = tipOption === "custom" 
    ? Number(customTip) 
    : tipOption === "none" 
      ? 0 
      : total * (Number(tipOption) / 100);

  const { isDarkMode } = useTheme();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-raya-card rounded-lg p-6 shadow-sm border border-raya-border">
        <h3 className="text-xl font-bold mb-4">Shipping Address</h3>

        <div className="grid grid-cols-2 gap-4">
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleInputChange}
            required
            className="col-span-1 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg focus:outline-none focus:ring-2 focus:ring-raya-accent/50 focus:border-raya-accent"
          />

          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleInputChange}
            required
            className="col-span-1 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg focus:outline-none focus:ring-2 focus:ring-raya-accent/50 focus:border-raya-accent"
          />

          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="col-span-2 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50 focus:border-raya-accent"
          />

          <input
            type="text"
            name="address"
            placeholder="Street Address"
            value={formData.address}
            onChange={handleInputChange}
            required
            className="col-span-2 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50 focus:border-raya-accent"
          />

          <input
            type="text"
            name="city"
            placeholder="City"
            value={formData.city}
            onChange={handleInputChange}
            required
            className="col-span-1 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg focus:outline-none focus:ring-2 focus:ring-raya-accent/50 focus:border-raya-accent"
          />

          <input
            type="text"
            name="state"
            placeholder="State"
            value={formData.state}
            onChange={handleInputChange}
            required
            className="col-span-1 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg focus:outline-none focus:ring-2 focus:ring-raya-accent/50 focus:border-raya-accent"
          />

          <input
            type="text"
            name="zip"
            placeholder="ZIP Code"
            value={formData.zip}
            onChange={handleInputChange}
            required
            className="col-span-1 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg focus:outline-none focus:ring-2 focus:ring-raya-accent/50 focus:border-raya-accent"
          />

          <input
            type="text"
            name="county"
            placeholder="County (Optional for SC)"
            value={formData.county}
            onChange={handleInputChange}
            className="col-span-1 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg focus:outline-none focus:ring-2 focus:ring-raya-accent/50 focus:border-raya-accent"
          />
        </div>
      </div>

      <div className="bg-raya-card rounded-lg p-6 shadow-sm border border-raya-border">
        <h3 className="text-xl font-bold mb-4">Payment Information</h3>

        <div className="p-4 border border-raya-border rounded-lg bg-raya-bg">
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                  base: {
                    color: isDarkMode ? "#F4F4F5" : "#18181B",
                    "::placeholder": {
                      color: isDarkMode ? "#A1A1AA" : "#71717A",
                    },
                  fontFamily: "system-ui, -apple-system, sans-serif",
                  fontSize: "16px",
                  iconColor: isDarkMode ? "#F4F4F5" : "#18181B",
                },
                invalid: {
                  color: "#ef4444",
                },
              },
            }}
          />
        </div>
      </div>

      <div className="bg-raya-card rounded-lg p-6 shadow-sm border border-raya-border">
        <h3 className="text-xl font-bold mb-4 flex items-center justify-between">
          <span>Add a Tip</span>
          <span className="text-raya-text-tertiary text-sm font-normal">Optional</span>
        </h3>
        
        <div className="grid grid-cols-4 gap-3 mb-4">
          {['10', '15', '20'].map(percent => (
            <button 
              key={percent}
              type="button"
              onClick={() => { setTipOption(percent); setCustomTip(0); }}
              className={`py-3 rounded-xl text-sm font-bold border transition-all ${tipOption === percent ? 'bg-raya-accent border-raya-accent text-black scale-105 shadow-md' : 'bg-raya-bg border-raya-border text-raya-text-secondary hover:border-raya-accent'}`}
            >
              {percent}%
            </button>
          ))}
          <button 
            type="button"
            onClick={() => setTipOption('custom')}
            className={`py-3 rounded-xl text-sm font-bold border transition-all ${tipOption === 'custom' ? 'bg-raya-accent border-raya-accent text-black scale-105 shadow-md' : 'bg-raya-bg border-raya-border text-raya-text-secondary hover:border-raya-accent'}`}
          >
            Custom
          </button>
        </div>

        {tipOption === 'custom' && (
          <div className="relative">
             <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-raya-text-tertiary">$</span>
             <input 
              type="number" 
              placeholder="0.00"
              className="w-full pl-8 pr-4 py-3 bg-raya-bg border border-raya-border rounded-xl outline-none focus:ring-2 focus:ring-raya-accent"
              value={customTip || ""}
              onChange={(e) => setCustomTip(Number(e.target.value))}
            />
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-raya-card rounded-lg p-6">
        <div className="space-y-2 mb-4">
          <div className="flex justify-between items-center text-raya-text-secondary">
            <span>Subtotal:</span>
            <span>${total.toFixed(2)}</span>
          </div>
          
          {taxDetails && taxDetails.taxBreakdown.length > 0 ? (
            <>
              {taxDetails.taxBreakdown.map((tax, idx) => (
                tax.amount > 0 && (
                  <div key={idx} className="flex justify-between items-center text-sm text-raya-text-secondary">
                    <span>{tax.name} ({tax.rate}):</span>
                    <span>${(tax.amount / 100).toFixed(2)}</span>
                  </div>
                )
              ))}
              {/* Only show "Total Tax" summary if there's actual tax */}
              {taxDetails.totalTax > 0 && (
                <div className="flex justify-between items-center pt-1 mt-1 border-t border-raya-border/50 text-raya-text-secondary font-medium">
                  <span>Total Tax:</span>
                  <span>${(taxDetails.totalTax / 100).toFixed(2)}</span>
                </div>
              )}
            </>
          ) : (
            <div className="flex justify-between items-center text-sm text-raya-text-secondary">
              <span>Tax:</span>
              <span>{loading ? "Calculating..." : "$0.00"}</span>
            </div>
          )}

          <div className="flex justify-between items-center pt-2 border-t border-raya-border">
            <span className="text-lg font-semibold">Grand Total:</span>
            <span className="text-2xl font-bold text-raya-accent">
              ${(total + (taxDetails?.totalTax || 0) / 100 + calculatedTip).toFixed(2)}
            </span>
          </div>
        </div>

        <button
          type="submit"
          disabled={!stripe || loading || !clientSecret}
          className="w-full bg-gradient-to-r from-raya-accent to-raya-accent-hover hover:from-raya-accent-hover hover:to-raya-accent disabled:opacity-50 text-raya-bg py-4 rounded-lg font-semibold transition transform hover:scale-[1.02] shadow-lg"
        >
          {loading ? "Processing..." : `Pay $${(total + (taxDetails?.totalTax || 0) / 100 + calculatedTip).toFixed(2)}`}
        </button>
      </div>
    </form>
  );
}

export default function Checkout() {
  const items = useCartStore((state) => state.items);

  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-xl text-raya-text-secondary">
          Your cart is empty
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-4xl font-bold mb-8">Checkout</h1>

      <Elements stripe={stripePromise}>
        <CheckoutForm />
      </Elements>
    </div>
  );
}
